import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Loader2, ArrowLeft, User, ChevronLeft, ChevronRight, Download, FileJson, FileSpreadsheet, BarChart2, List } from 'lucide-react';
import { fetchSurveyById, fetchSurveyResponsesPaginated, fetchAllSurveyResponsesForExport } from '../services/surveyService';
import { Survey, SurveyResponse } from '../types';

const SurveyResponsesView: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    
    const [survey, setSurvey] = useState<Survey | null>(null);
    const [responses, setResponses] = useState<SurveyResponse[]>([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const [isExporting, setIsExporting] = useState(false);
    const LIMIT = 10;

    useEffect(() => {
        if (id) {
            loadData();
        }
    }, [id, page]);

    const loadData = async () => {
        setLoading(true);
        try {
            if (!survey) {
                const s = await fetchSurveyById(id!);
                setSurvey(s);
            }
            const { data, count } = await fetchSurveyResponsesPaginated(id!, page, LIMIT);
            setResponses(data);
            setTotalCount(count);
        } catch (e) {
            console.error("Failed to load data", e);
        } finally {
            setLoading(false);
        }
    };

    const handleExport = async (format: 'csv' | 'json') => {
        if (!id || isExporting) return;
        setIsExporting(true);
        try {
            // Fetch ALL data for export (handle large datasets via service)
            const allData = await fetchAllSurveyResponsesForExport(id);
            
            if (format === 'json') {
                const jsonString = JSON.stringify(allData, null, 2);
                const blob = new Blob([jsonString], { type: 'application/json' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `survey_responses_${id}_${new Date().toISOString()}.json`;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                URL.revokeObjectURL(url);
            } else if (format === 'csv') {
                // Flatten Data for CSV
                // 1. Collect all unique Question IDs to form columns
                const questionMap = new Map<string, string>(); // ID -> Text
                allData.forEach(r => {
                    r.answers?.forEach(a => {
                        if (a.question_id && a.question?.question_text) {
                            questionMap.set(a.question_id, getLocalizedText(a.question.question_text));
                        }
                    });
                });

                const headers = ['Response ID', 'User ID', 'User Name', 'User Email', 'Status', 'Completed At', ...Array.from(questionMap.values())];
                const csvRows = [headers.join(',')];

                for (const r of allData) {
                    const row = [
                        r.id,
                        r.user_id || 'Anonymous',
                        r.user?.full_name || 'Guest',
                        r.user?.email || '',
                        r.status,
                        r.completed_at
                    ];

                    // Add answers in order of headers
                    for (const [qId] of questionMap) {
                        const answer = r.answers?.find(a => a.question_id === qId);
                        let val = answer?.answer_value;
                        
                        // Handle complex types for CSV
                        if (typeof val === 'object' && val !== null) {
                            val = JSON.stringify(val).replace(/"/g, '""'); // Escape quotes
                        } else if (typeof val === 'string') {
                            val = val.replace(/"/g, '""');
                        }
                        
                        row.push(`"${val !== undefined && val !== null ? val : ''}"`);
                    }
                    csvRows.push(row.join(','));
                }

                const csvString = csvRows.join('\n');
                const blob = new Blob([csvString], { type: 'text/csv' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `survey_responses_${id}_${new Date().toISOString()}.csv`;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                URL.revokeObjectURL(url);
            }

        } catch (e) {
            console.error("Export failed", e);
            alert("Export failed. Please try again.");
        } finally {
            setIsExporting(false);
        }
    };

    const totalPages = Math.ceil(totalCount / LIMIT);

    // Helper to get localized text
    const getLocalizedText = (obj: any) => obj?.en || Object.values(obj)[0] || 'Untitled';

    return (
        <div className="space-y-6 animate-in fade-in duration-300">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                    <button onClick={() => navigate('/research/campaigns')} className="p-2 hover:bg-slate-800 rounded-lg text-slate-400 hover:text-white transition-colors">
                        <ArrowLeft size={20} />
                    </button>
                    <div>
                        <h1 className="text-xl font-bold text-white">{survey ? getLocalizedText(survey.title) : 'Survey Responses'}</h1>
                        <p className="text-slate-400 text-sm">Total Responses: {totalCount}</p>
                    </div>
                </div>
                
                {/* Tabs + Export Actions */}
                <div className="flex items-center gap-2">
                    <div className="flex items-center gap-1 bg-slate-800 rounded-lg p-1 mr-1">
                        <button onClick={() => navigate(`/research/campaigns/${id}/dashboard`)} className="px-3 py-1.5 rounded-md text-sm font-medium text-slate-400 hover:text-white flex items-center gap-1.5"><BarChart2 size={15} /> Dashboard</button>
                        <span className="px-3 py-1.5 rounded-md text-sm font-medium bg-indigo-600 text-white flex items-center gap-1.5"><List size={15} /> Responses</span>
                    </div>
                    <button 
                        onClick={() => handleExport('csv')} 
                        disabled={isExporting || totalCount === 0}
                        className="flex items-center gap-2 px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-lg border border-slate-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {isExporting ? <Loader2 size={16} className="animate-spin" /> : <FileSpreadsheet size={16} />}
                        <span className="text-sm font-medium">Export CSV</span>
                    </button>
                    <button 
                        onClick={() => handleExport('json')} 
                        disabled={isExporting || totalCount === 0}
                        className="flex items-center gap-2 px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-lg border border-slate-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {isExporting ? <Loader2 size={16} className="animate-spin" /> : <FileJson size={16} />}
                        <span className="text-sm font-medium">Export JSON</span>
                    </button>
                </div>
            </div>

            {/* Content */}
            {loading && responses.length === 0 ? (
                <div className="flex justify-center py-12"><Loader2 className="animate-spin text-indigo-500" /></div>
            ) : (
                <div className="bg-slate-800 rounded-xl border border-slate-700 overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full text-left text-sm text-slate-400">
                            <thead className="bg-slate-900/50 text-slate-200 uppercase font-bold text-xs">
                                <tr>
                                    <th className="px-6 py-4">User</th>
                                    <th className="px-6 py-4">Status</th>
                                    <th className="px-6 py-4">Date</th>
                                    <th className="px-6 py-4">Answers</th>
                                    <th className="px-6 py-4 text-right">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-700">
                                {responses.length === 0 ? (
                                    <tr>
                                        <td colSpan={4} className="px-6 py-8 text-center text-slate-500 italic">No responses yet.</td>
                                    </tr>
                                ) : (
                                    responses.map((r) => (
                                        <tr key={r.id} className="hover:bg-slate-700/30 transition-colors">
                                            <td className="px-6 py-4">
                                                <div className="flex items-center gap-2">
                                                    <div className="w-8 h-8 rounded-full bg-indigo-500/20 flex items-center justify-center text-indigo-400">
                                                        <User size={14} />
                                                    </div>
                                                    <div>
                                                        <p className="text-white font-medium">{r.user?.full_name || 'Anonymous'}</p>
                                                        <p className="text-xs text-slate-500">{r.user?.email || r.user_id || 'Guest'}</p>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4">
                                                <span className="px-2 py-1 rounded text-xs font-medium bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
                                                    {r.status}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 font-mono text-xs">
                                                {new Date(r.completed_at).toLocaleString()}
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className="flex flex-col gap-1">
                                                    {r.answers?.slice(0, 3).map((a, i) => (
                                                        <div key={i} className="text-xs truncate max-w-xs" title={String(a.answer_value)}>
                                                            <span className="text-slate-500 mr-1">{i+1}.</span>
                                                            <span className="text-slate-300">{String(a.answer_value)}</span>
                                                        </div>
                                                    ))}
                                                    {(r.answers?.length || 0) > 3 && (
                                                        <span className="text-xs text-indigo-400 italic">+{ (r.answers?.length || 0) - 3 } more...</span>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 text-right">
                                                <button 
                                                    onClick={() => navigate(`/research/responses/${r.id}`)}
                                                    className="text-indigo-400 hover:text-indigo-300 text-xs font-medium hover:underline"
                                                >
                                                    View Details
                                                </button>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination */}
                    {totalPages > 1 && (
                        <div className="p-4 border-t border-slate-700 flex items-center justify-between bg-slate-900/30">
                            <button 
                                onClick={() => setPage(p => Math.max(1, p - 1))}
                                disabled={page === 1}
                                className="p-2 rounded hover:bg-slate-700 disabled:opacity-50 disabled:hover:bg-transparent text-slate-400"
                            >
                                <ChevronLeft size={20} />
                            </button>
                            <span className="text-sm text-slate-400">Page {page} of {totalPages}</span>
                            <button 
                                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                                disabled={page === totalPages}
                                className="p-2 rounded hover:bg-slate-700 disabled:opacity-50 disabled:hover:bg-transparent text-slate-400"
                            >
                                <ChevronRight size={20} />
                            </button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default SurveyResponsesView;
